import { useState } from 'react';
import { AlertTriangle, CalendarCheck } from 'lucide-react';
import type { Asunto } from '@/api/asuntos.api';
import { TarjetaDeAsunto } from '@/components/athena/TarjetaDeAsunto';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { fechaLarga } from '@/lib/athena';
import { cn } from '@/lib/cn';
import { alSoltar, type AccionPrincipal, type GrupoDeAsuntos } from '@/lib/metoca';

/**
 * LAS DOS VISTAS NUEVAS DE «ME TOCA» (tanda 6): por día y tablero.
 *
 * Las dos dibujan la MISMA `TarjetaDeAsunto` de la lista: lo que cambia es cómo
 * se agrupan (`lib/metoca`), no qué es un asunto ni qué se hace con él. Abrir,
 * aprobar o marcar hecho, descartar y posponer llegan por las mismas funciones
 * a las tres vistas.
 */

/** Lo que se puede hacer con un asunto desde cualquier vista: los mismos nombres que usa la tarjeta. */
export interface AccionesDeAsunto {
  onAbrir: (id: string) => void;
  onPrincipal?: (id: string, accion: AccionPrincipal) => void;
  rotuloPrincipal?: string;
  /** Ir a Seguimientos a leer un mensaje para el cliente (solo si quien mira tiene esa pantalla). */
  onRevisar?: () => void;
  onDescartar?: (id: string) => void;
  onPosponer?: (id: string, hasta: string) => void;
  onVerMensaje?: (a: Asunto) => void;
}

const TONO: Record<GrupoDeAsuntos['tono'], { titulo: string; cuenta: string; punto: string; tarjeta: string }> = {
  alerta: {
    titulo: 'text-red-700 dark:text-red-300',
    cuenta: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200',
    punto: 'bg-red-500',
    tarjeta: 'border-red-200 dark:border-red-900/60',
  },
  hoy: {
    titulo: 'text-brand-700 dark:text-brand-300',
    cuenta: 'bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200',
    punto: 'bg-brand-500',
    tarjeta: 'border-brand-300 ring-1 ring-brand-200 dark:border-brand-700 dark:ring-brand-900',
  },
  normal: {
    titulo: 'text-slate-800 dark:text-slate-100',
    cuenta: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
    punto: 'bg-slate-400',
    tarjeta: '',
  },
  apagado: {
    titulo: 'text-slate-600 dark:text-slate-400',
    cuenta: 'bg-slate-200/70 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
    punto: 'bg-slate-300 dark:bg-slate-600',
    tarjeta: '',
  },
};

const cuantos = (n: number) => (n === 1 ? '1 asunto' : `${n} asuntos`);

/** El título de un grupo con su contador. La alerta no se dice solo con color: lleva su icono. */
function Encabezado({ grupo, id }: { grupo: GrupoDeAsuntos; id?: string }) {
  const tono = TONO[grupo.tono];
  return (
    <>
      {grupo.tono === 'alerta' ? (
        <AlertTriangle aria-hidden className={cn('h-4 w-4 shrink-0', tono.titulo)} />
      ) : (
        <span aria-hidden className={cn('h-2 w-2 shrink-0 rounded-full', tono.punto)} />
      )}
      <h2 id={id} className={cn('min-w-0 truncate text-sm font-semibold', tono.titulo)}>
        {grupo.titulo}
      </h2>
      <span title={cuantos(grupo.asuntos.length)} className={cn('shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums', tono.cuenta)}>
        {grupo.asuntos.length}
      </span>
    </>
  );
}

/** La fecha que acompaña a «Hoy» y «Mañana»: ubica sin abrir el calendario. */
function fechaDelGrupo(id: string, ahora: Date): string | null {
  if (id === 'hoy') return fechaLarga(ahora);
  if (id === 'manana') return fechaLarga(new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate() + 1));
  return null;
}

/**
 * POR DÍA: «¿qué hago ahora?». Vencidos en alerta, Hoy destacado, y lo demás
 * por cuándo toca. Dentro, la tarjeta de siempre con todos sus gestos.
 * Sin estado: se prueba con renderToStaticMarkup.
 */
export function VistaPorDia({
  grupos,
  ahora,
  vacio,
  ...acciones
}: { grupos: GrupoDeAsuntos[]; ahora: Date; vacio: { titulo: string; detalle?: string } } & AccionesDeAsunto) {
  if (grupos.length === 0) return <EmptyState icon={CalendarCheck} title={vacio.titulo} description={vacio.detalle} />;
  const uno = grupos.length === 1;
  const seccion = (g: GrupoDeAsuntos) => {
    const fecha = fechaDelGrupo(g.id, ahora);
    return (
      <section key={g.id} data-grupo={g.id} aria-labelledby={`dia-${g.id}`} className="min-w-0">
        <header className="mb-1.5 flex items-center gap-2 px-1">
          <Encabezado grupo={g} id={`dia-${g.id}`} />
          {fecha && <span className="ml-auto shrink-0 text-xs text-slate-500 first-letter:uppercase dark:text-slate-400">{fecha}</span>}
        </header>
        {/* Con un solo grupo, en escritorio sus tarjetas van a dos columnas, como en la lista. */}
        <Card className={cn('overflow-hidden', TONO[g.tono].tarjeta, uno && 'xl:columns-2 xl:gap-0 [&>*]:break-inside-avoid')}>
          {g.asuntos.map((a) => (
            // En «Hoy» y «Mañana» el día ya lo dice el título: la tarjeta da la hora.
            <TarjetaDeAsunto key={a.id} asunto={a} conFecha={!fecha} {...acciones} />
          ))}
        </Card>
      </section>
    );
  };
  if (uno) return seccion(grupos[0]!);
  // En escritorio, lo primero (lo vencido, o lo de hoy si no hay nada vencido) a la izquierda y lo
  // que viene después a la derecha: así «Vencidos» y «Hoy» se ven a la vez, sin bajar. En el
  // teléfono es una sola columna, en el mismo orden.
  const [primero, ...resto] = grupos;
  return (
    <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 xl:grid-cols-2">
      {seccion(primero!)}
      <div className="min-w-0 space-y-5">{resto.map(seccion)}</div>
    </div>
  );
}

/** El tipo propio del arrastre: una columna solo recibe tarjetas de este tablero, no un archivo ni un texto cualquiera. */
const TIPO_DE_ARRASTRE = 'application/x-conversia-asunto';

const SI_ESTA_VACIA: Record<string, string> = {
  'por-aprobar': 'Nada por confirmar. Lo que Athena detecte en tus chats cae aquí.',
  'por-hacer': 'Nada por hacer. Lo que apruebes o anotes con el «+» cae aquí.',
  esperando: 'Nadie te debe respuesta ahora mismo.',
  vencido: 'Nada vencido. Vas al día.',
};

/**
 * EL TABLERO: «¿en qué va cada cosa?». Cuatro columnas del flujo, siempre las
 * cuatro. En el teléfono se pasan de lado, una casi completa a la vez; desde
 * `xl` caben las cuatro.
 *
 * Arrastrar una tarjeta de «Por aprobar» a «Por hacer» la aprueba, y es el
 * único arrastre que hace algo (`alSoltar`): por eso solo esas tarjetas se
 * dejan agarrar. Un mensaje para el cliente no se arrastra: aprobarlo lo manda
 * por WhatsApp, y eso se hace en Seguimientos, con el texto a la vista. Arrastre
 * nativo del navegador, sin librerías. En el teléfono no hay arrastre: cada
 * tarjeta trae a la vista su «Aprobar» o su «Hecho», que hacen lo mismo y son
 * también el camino con el teclado.
 *
 * Solo recuerda qué tarjeta va en el aire y sobre qué columna está.
 */
export function VistaTablero({
  columnas,
  fuera = 0,
  onVerLista,
  ...acciones
}: {
  columnas: GrupoDeAsuntos[];
  /** Cuántos asuntos del filtro no caben en ninguna columna (descartados, cumplidos). */
  fuera?: number;
  onVerLista?: () => void;
} & AccionesDeAsunto) {
  const [enElAire, setEnElAire] = useState<string | null>(null);
  const [sobre, setSobre] = useState<string | null>(null);
  const { onPrincipal, rotuloPrincipal } = acciones;
  const todos = columnas.flatMap((c) => c.asuntos);
  const arrastrada = enElAire ? (todos.find((a) => a.id === enElAire) ?? null) : null;
  // Cuando el botón principal hace otra cosa (rescatar, en «Ruido»), arrastrar no es aprobar: no se ofrece.
  const seArrastra = (a: Asunto) => Boolean(onPrincipal) && !rotuloPrincipal && alSoltar(a, 'por-hacer') !== null;
  const dejar = () => {
    setEnElAire(null);
    setSobre(null);
  };

  return (
    <div className="space-y-3">
      <div className="-mx-4 flex snap-x snap-mandatory scroll-px-4 gap-3 overflow-x-auto px-4 pb-2 md:mx-0 md:scroll-px-0 md:px-0 xl:overflow-visible">
        {columnas.map((c) => {
          const acepta = arrastrada ? alSoltar(arrastrada, c.id) !== null : false;
          const encima = acepta && sobre === c.id;
          return (
            <section
              key={c.id}
              data-columna={c.id}
              data-soltar={encima ? 'si' : undefined}
              aria-labelledby={`columna-${c.id}`}
              onDragOver={(e) => {
                if (!acepta || !Array.from(e.dataTransfer.types).includes(TIPO_DE_ARRASTRE)) return;
                // Sin `preventDefault` el navegador no deja soltar: es lo que se quiere en las demás columnas.
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                setSobre(c.id);
              }}
              onDragLeave={(e) => {
                // Pasar por encima de una tarjeta de la columna no es salir de la columna.
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setSobre((s) => (s === c.id ? null : s));
              }}
              onDrop={(e) => {
                e.preventDefault();
                const id = e.dataTransfer.getData(TIPO_DE_ARRASTRE) || enElAire;
                const soltada = todos.find((a) => a.id === id);
                dejar();
                if (soltada && alSoltar(soltada, c.id) === 'aprobar') onPrincipal?.(soltada.id, 'aprobar');
              }}
              className={cn(
                'flex w-[86%] shrink-0 snap-start flex-col overflow-hidden rounded-xl border sm:w-80 xl:w-auto xl:min-w-0 xl:flex-1',
                // Con muchas tarjetas se desplaza la columna y no la página: los encabezados siguen a la vista.
                'max-h-[max(22rem,calc(100dvh-19rem-var(--alto-de-pestanas,0px)))] md:max-h-[max(24rem,calc(100dvh-15rem-var(--alto-de-pestanas,0px)))]',
                'transition-[background-color,border-color,box-shadow] duration-150',
                encima
                  ? 'border-brand-400 bg-brand-50 ring-2 ring-brand-400/40 dark:bg-brand-900/20'
                  : acepta
                    ? 'border-dashed border-brand-400 bg-slate-100/80 dark:bg-slate-900/60'
                    : 'border-surface-border bg-slate-100/80 dark:border-slate-800 dark:bg-slate-900/60',
              )}
            >
              <header className="flex items-center gap-2 px-3 py-2.5">
                <Encabezado grupo={c} id={`columna-${c.id}`} />
              </header>
              {acepta && (
                <p role="status" className="mx-2 mb-2 rounded-lg border border-dashed border-brand-300 px-2 py-2 text-center text-xs font-medium text-brand-700 dark:border-brand-700 dark:text-brand-200">
                  Suéltala aquí para aprobarla
                </p>
              )}
              {/* El relleno de arriba le deja sitio a la tarjeta que sube un punto con el cursor. */}
              <ul className="min-h-0 flex-1 space-y-2 overflow-y-auto px-2 pb-2 pt-0.5">
                {c.asuntos.length === 0 ? (
                  <li className="rounded-lg border border-dashed border-slate-300 px-3 py-5 text-center text-xs leading-relaxed text-slate-600 dark:border-slate-700 dark:text-slate-400">
                    {SI_ESTA_VACIA[c.id] ?? 'Nada aquí.'}
                  </li>
                ) : (
                  c.asuntos.map((a) => {
                    const agarre = seArrastra(a);
                    return (
                      <li key={a.id}>
                        <article
                          data-asunto={a.id}
                          draggable={agarre}
                          onDragStart={(e) => {
                            e.dataTransfer.effectAllowed = 'move';
                            e.dataTransfer.setData(TIPO_DE_ARRASTRE, a.id);
                            setEnElAire(a.id);
                          }}
                          onDragEnd={dejar}
                          className={cn(
                            'overflow-hidden rounded-lg border border-surface-border bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900',
                            'transition-[transform,box-shadow,opacity] duration-200 ease-out hover:shadow-md motion-safe:hover:-translate-y-0.5',
                            // Se apaga en su sitio cuando ya va sobre la columna que la recibe, no al agarrarla:
                            // el navegador le toma justo entonces la foto que sigue al cursor.
                            a.id === enElAire && sobre && 'opacity-40',
                          )}
                        >
                          {/* Sin deslizar: en el tablero, de lado se cambia de columna. Tocarla abre la hoja, con todo lo demás. */}
                          <TarjetaDeAsunto asunto={a} compacta agarre={agarre} conGestos={false} {...acciones} />
                        </article>
                      </li>
                    );
                  })
                )}
              </ul>
            </section>
          );
        })}
      </div>
      {fuera > 0 && (
        <p className="text-xs text-slate-600 dark:text-slate-400">
          {fuera === 1 ? '1 asunto de este filtro no sale' : `${fuera} asuntos de este filtro no salen`} en el tablero porque ya{' '}
          {fuera === 1 ? 'está cerrado o descartado' : 'están cerrados o descartados'}.{' '}
          {onVerLista && (
            <button type="button" onClick={onVerLista} className="font-medium text-brand-700 underline underline-offset-2 dark:text-brand-300">
              {fuera === 1 ? 'Verlo en la lista' : 'Verlos en la lista'}
            </button>
          )}
        </p>
      )}
    </div>
  );
}
